import { toNativePromise } from '~commonInfra/utils/QUtils';
import { LoginUrlsWrapper } from '../../../app/spaces/Login/LoginUrls';
import { Location } from '../../../common/react/Location';
import { IError } from '../../../httpClient/serverApi/types/IError';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../../settings/services/ServerSettingsUnauthenticated';
import { StartPageService } from '../../../settings/services/StartPageService';
import { CommonErrorCode } from '../../helpers/ErrorCodes';

function getSiteUrlFragment(urlName: string): string {
  return `/#/site/${urlName}/`;
}

function getSitePathFragment(urlName: string): string {
  return `/site/${urlName ?? ''}`;
}

/**
* Switches the site if the given urlName if it's not the current site's urlName
*
* Use this instead of SessionManagementService.switchSiteIfNeeded in order to give proper
* feedback in the error conditions
*/
function switchIfNeeded(urlName: string, podRedirectPath?: string): Promise<void> {
  if (podRedirectPath === undefined) {
    podRedirectPath = getSiteUrlFragment(urlName);
  }

  return toNativePromise(getSessionManagementServiceInstance().switchSiteIfNeeded_DoNotCallDirectly(urlName, podRedirectPath)
    .catch((error: IError) => {
      // This error code is returned when the current user doesn't have access to the site they're trying to switch to.
      // The user must log in using different credentials, specific to the site. For now, it is supported only when
      // third party auth is configured (i.e. SiteSAML is enabled).
      if (error && error.code === CommonErrorCode.SiteUserNotFound && ServerSettingsUnauthenticated.isSiteSamlEnabled()) {
        LoginUrlsWrapper.navigateToSignIn({
          site: urlName
        });
      } else {
        LoginUrlsWrapper.navigateToSignInErrorCodeFromSiteSwitch('' + error.code);
      }
      return Promise.reject(error);
    }));
}

function switchIfNeededAndRedirectToStartPage(urlName: string, podRedirectPath?: string): Promise<void> {
  return switchIfNeeded(urlName, podRedirectPath).then(() => {
    Location.instance.setHash(StartPageService.getCurrentUserStartPageForCurrentSite());
  });
}

function switchIfNeededNoFeedback(urlName: string): Promise<void> {
  return toNativePromise(getSessionManagementServiceInstance().switchSiteIfNeeded_DoNotCallDirectly(urlName));
}

export const SiteSwitchService = {
  getSiteUrlFragment,
  getSitePathFragment,
  switchIfNeeded,
  switchIfNeededAndRedirectToStartPage,
  switchIfNeededNoFeedback
};
